import { Stack, Text, Icon, FormControl, FormErrorMessage, Button } from '@chakra-ui/react';
import SmallSectionTitleComponent from '@components/SmallSectionTitleComponent/SmallSectionTitleComponent';
import palette from '@theme/assets/palette';
import { FC, useCallback, useMemo } from 'react';
import Dropzone, { Accept } from 'react-dropzone';
import { Control, Controller, FieldError, FieldErrorsImpl, FieldValues, Merge } from 'react-hook-form';
import { MdDelete, MdUpload } from 'react-icons/md';
import { FileData } from 'types/formData';

interface Props {
    id: string;
    title: string;
    subtitle: string;
    maxSize?: number;
    uploadedFile: File[] | File | FileData[] | FileData | null;
    updateFile: (files: File[]) => void;
    deleteFile: () => void;
    control: Control<FieldValues>;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | null;
    isInvalid?: boolean;
    isRequired?: boolean;
    sizeString?: string;
}

const UploadComponent: FC<Props> = ({
    id,
    title,
    subtitle,
    control,
    error = null,
    isInvalid = false,
    maxSize = 3000000,
    sizeString = '3 MB',
    updateFile,
    deleteFile,
    uploadedFile,
    isRequired = false,
}) => {
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            updateFile(acceptedFiles);
        },
        [updateFile],
    );

    const validator = useCallback(
        (file: File) => {
            if (maxSize && file.size > maxSize) {
                return {
                    code: 'file-too-big',
                    message: `Le dimensioni del file non possono superare i 3MB`,
                };
            }
            // const fileFound = uploadedFiles.find((fileUploaded) => fileUploaded.name === file.name)
            // if (fileFound) {
            //     return {
            //         code: 'file-existent',
            //         message: 'File esistente'
            //     }
            // }

            return null;
        },
        [maxSize],
    );

    const accept: Accept = useMemo(() => {
        return {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        };
    }, []);

    const uploadedFilesCount = useMemo(() => {
        if (uploadedFile === null) {
            return 0;
        } else if (Array.isArray(uploadedFile)) {
            return uploadedFile.length;
        }
        return uploadedFile.size > 0 ? 1 : 0;
    }, [uploadedFile]);

    return (
        <FormControl isInvalid={isInvalid}>
            <Stack w="100%" direction="column" spacing="20px">
                <SmallSectionTitleComponent title={title} />
                <Text variant="input" w="auto" color="gray.600">
                    {subtitle}
                </Text>
                <Controller
                    name={id}
                    control={control}
                    rules={{ required: isRequired ? 'Questo campo è obbligatorio' : false }}
                    defaultValue={uploadedFile}
                    render={({ field: { onChange, onBlur } }) => {
                        return (
                            <Dropzone
                                onDrop={(acceptedFiles) => {
                                    if (uploadedFilesCount === 0) {
                                        onChange(acceptedFiles);
                                        onDrop(acceptedFiles);
                                    }
                                }}
                                noClick
                                noKeyboard
                                accept={accept}
                                multiple={false}
                                maxSize={maxSize}
                                validator={validator}
                            >
                                {({ getRootProps, getInputProps, open }) => (
                                    <Stack
                                        direction="column"
                                        align="center"
                                        w="100%"
                                        minH="72px"
                                        spacing="0"
                                        borderRadius="6px"
                                        position="relative"
                                        zIndex="1"
                                        border={'1px dashed'}
                                        borderStyle={'dashed'}
                                        borderColor={'#D1D5DB'}
                                        cursor={uploadedFilesCount === 0 ? 'pointer' : 'inherit'}
                                        {...getRootProps()}
                                        onClick={() => {
                                            if (uploadedFilesCount === 0) {
                                                open();
                                            }
                                        }}
                                    >
                                        <input type="hidden" {...getInputProps({ onChange, onBlur })} />
                                        <Stack
                                            direction="column"
                                            align="center"
                                            w="100%"
                                            minH="72px"
                                            bg="transparent"
                                            borderRadius="6px"
                                            spacing="0"
                                            p="32px 16px"
                                            position="relative"
                                            zIndex="3"
                                        >
                                            <Icon
                                                as={MdUpload}
                                                w={'32px'}
                                                h={'32px'}
                                                color={uploadedFilesCount > 0 ? palette.brand01 : '#6B7280'}
                                            />
                                            <Stack direction="column" w="auto" flex="0 1 auto" spacing="0" pl="10px">
                                                {uploadedFilesCount > 0 ? (
                                                    <>
                                                        <Text
                                                            variant="subtitle"
                                                            display={'inline-block'}
                                                            color={'#1a1a1a'}
                                                            width="auto"
                                                            transition="color 0.22s linear"
                                                            flex="0 1 auto"
                                                            textAlign={'center'}
                                                        >
                                                            {uploadedFilesCount} file caricato
                                                        </Text>
                                                        <Text
                                                            variant="text"
                                                            display={'inline-block'}
                                                            color={'rgba(33,37,41,0.5)'}
                                                            width="auto"
                                                            transition="color 0.22s linear"
                                                            flex="0 1 auto"
                                                            textAlign={'center'}
                                                        >
                                                            {Array.isArray(uploadedFile)
                                                                ? uploadedFile.map((file) => {
                                                                      return file.name;
                                                                  })
                                                                : (uploadedFile?.name ?? '')}
                                                        </Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Text
                                                            variant="subtitle"
                                                            display={'inline-block'}
                                                            color={'#1a1a1a'}
                                                            width="auto"
                                                            transition="color 0.22s linear"
                                                            flex="0 1 auto"
                                                            textAlign={'center'}
                                                        >
                                                            Clicca qui o trascina un file all'interno per caricare un
                                                            file
                                                        </Text>
                                                        <Text
                                                            variant="text"
                                                            display={'inline-block'}
                                                            color={'rgba(33,37,41,0.5)'}
                                                            width="auto"
                                                            transition="color 0.22s linear"
                                                            flex="0 1 auto"
                                                            textAlign={'center'}
                                                        >
                                                            PDF, DOC fino a {sizeString}
                                                        </Text>
                                                    </>
                                                )}
                                            </Stack>
                                            {uploadedFilesCount > 0 ? (
                                                <Button
                                                    leftIcon={<Icon as={MdDelete} size={16} />}
                                                    variant="link"
                                                    color={palette.ui06}
                                                    marginTop="10px"
                                                    onClick={deleteFile}
                                                    fontSize={16}
                                                    alignItems="center"
                                                >
                                                    Rimuovi file
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Stack>
                                )}
                            </Dropzone>
                        );
                    }}
                />
            </Stack>
            {error ? (
                <FormErrorMessage position="absolute" fontSize={12}>
                    {error?.message?.toString()}
                </FormErrorMessage>
            ) : (
                ''
            )}
        </FormControl>
    );
};

export default UploadComponent;
